"use client";

// ── FOUNDER MAP RULING, 2026-08-27 · THE SCHEDULE INDEX GETS A REAL BASEMAP, IMMEDIATELY ──────────
//
// THE RULING, and each clause is answered below:
//   "The schedule-index page must mount a functional basemap immediately."
//   At rest: "a neutral Vancouver–Whistler corridor view", and "do not display schedule-specific
//   stops, route lines or fabricated markers."
//   "Derive bounds only from an existing authoritative place/stop registry. If none exists, use the
//   approved map provider's regional view without markers."
//   "Do not use transport-stops.stub anywhere visitor-facing."
//
// WHAT WAS THERE BEFORE. The design export ships a 6x4 grid of 24 static PNG tiles inside an
// `aria-hidden` container — a picture of a map. It is the placeholder the ruling names. Measured
// 2026-08-27 on the served build: the page produced `leaflet=0, tiles=0` — no map object, no tile
// request, nothing that responds to a visitor at all. (The 24 tiles are NOT broken, which was the
// tempting wrong answer: each answers HTTP 200 and reads `naturalWidth 0` only because it is
// `loading="lazy"` and off-screen. The defect was never a dead asset — it was that no map existed.)
//
// WHY NO MARKERS AT REST, and why that is the honest answer rather than a thin one. There is no
// authoritative coordinate registry in this tree to derive bounds from: `place-names.ts` binds
// authored names to engine stop CODES and carries no lat/lng, `airport-places.ts` carries none, and
// the only two coordinate sources are `transport-stops.stub.ts` — which the ruling forbids on a
// visitor-facing surface — and `/api/transport/geo`, which reads the legacy RDS and does not answer
// outside the VPC. The engine's `stopDetails[].location` IS authoritative, but it is returned per
// ROUTE and DATE and this page has neither. So this is the ruling's second branch: the approved
// provider's regional view, no markers. The framing constant lives in `map-config.ts` beside the
// reasoning that it is a viewport and not data.
//
// AND WHERE THE MARKERS DO COME FROM. The moment a route and date exist, the results screen fits the
// map to the engine's own returned stops — see `BookingResults`, which consults
// `stopDetails[].location` before anything else. This layer is the AT-REST half of that story, not a
// substitute for it.

import { useEffect } from "react";
import { createRoot, type Root } from "react-dom/client";
// finding 8 (cold load) — code-split boundary; see KeylessMapLazy. This layer is mounted on the
// schedule index only, and its map chunk now downloads when that layer actually renders one.
import KeylessMap from "./KeylessMapLazy";
import { MAP_CORRIDOR_VIEW } from "@/lib/map-config";

export default function CorridorBasemapLayer() {
  useEffect(() => {
    let disposed = false;
    let cleanup: (() => void) | null = null;

    // ── WAIT FOR THE DESIGN, DO NOT RACE IT ────────────────────────────────────────────────────────
    // MEASURED 2026-08-27, and it is why the first cut of this layer did nothing at all on the served
    // build: `DesignSurface` fetches and rewrites the 1.1MB export and injects it AFTER mount, so on
    // the first effect tick the mosaic does not exist yet. A one-shot querySelector therefore found
    // nothing and returned FOREVER — the page kept its 24 static tiles and reported `leaflet=0,
    // tiles=0` exactly as before the layer was written (probe: corridorHost=0, tileImgs=24).
    // A MutationObserver waits for the export instead of guessing when it lands, and gives up after a
    // bounded wait so a page that genuinely has no mosaic costs nothing and leaks no observer.
    const attach = (tile: HTMLImageElement): boolean => {
      const mosaic = tile.parentElement;
      if (!mosaic) return false;

      // The region the mosaic fills. The export gates it on a `display` binding that can resolve to
      // `none`, and the ruling says the basemap mounts IMMEDIATELY — so the region is made visible
      // here. Nothing else about it is touched: its height, background and position are the design's.
      const region = mosaic.parentElement;
      if (!region) return false;
      const prevRegionDisplay = region.style.display;
      if (getComputedStyle(region).display === "none") region.style.display = "block";

      // The picture of a map goes away rather than sitting under a real one — two maps stacked is a
      // worse answer than either alone, and a static crop under a live map reads as a rendering fault.
      const prevMosaicDisplay = mosaic.style.display;
      mosaic.style.display = "none";

      const host = document.createElement("div");
      host.setAttribute("data-corridor-basemap", "");
      host.style.cssText = "position:absolute;inset:0;";
      region.appendChild(host);

      const root: Root = createRoot(host);
      // markers=[] is the point of this layer, not an oversight: the ruling forbids schedule-specific
      // stops, route lines and fabricated markers on this surface.
      root.render(<KeylessMap markers={[]} view={MAP_CORRIDOR_VIEW} surface="schedule-index-corridor" />);

      cleanup = () => {
        // Unmount asynchronously: React refuses to unmount a root synchronously from inside the
        // cleanup that is itself running during a render pass, and warns loudly when you try.
        setTimeout(() => root.unmount(), 0);
        host.remove();
        mosaic.style.display = prevMosaicDisplay;
        region.style.display = prevRegionDisplay;
      };
      return true;
    };

    // ── FOUNDER-WALK-LIVE-RECONCILE-025 · S4 — NEVER THE MOUNTAIN SELECTOR'S OWN PANE ────────────
    // MEASURED IN THE EXPORT, not assumed: `assets/map/tiles/z5-*.png` occurs in exactly ONE place in
    // sg-beta-v2.01.html — the "Pick your mountain" panel's map pane (`<section data-sg-menu="mtn">`,
    // selector map has been removed. There is no schedule-index mosaic at all. So every
    // time this layer "found the mosaic" it found the selector's, forced that pane's `display` to
    // block, drew the corridor view under the design's PERCENTAGE-POSITIONED resort pins (their
    // `left`/`top` are Web-Mercator fractions of the old static tile rectangle), and
    // handed the founder a pannable map whose pins stay put: the "pins not geographically attached
    // on pan and zoom" finding. page.tsx already recorded this collision (the schedule-index note
    // beside the gated mount) and the mountain roots still mounted the layer unconditionally.
    // The selector's pane is therefore excluded by its owner rather than by a class name the design
    // could rename; on the design's own header selector the map is now the host's `KeylessMap`
    // (see founder-walk-live-reconcile-025-export-patch.mjs · S1). With no other mosaic in the
    // export this layer finds nothing and its bounded observer stands down — the honest outcome,
    // recorded rather than dressed up as a basemap it never actually provided.
    const find = () => {
      for (const img of Array.from(document.querySelectorAll<HTMLImageElement>('img[src*="assets/map/tiles/"]'))) {
        if (img.closest('[data-sg-menu="mtn"]')) continue;
        return img;
      }
      return null;
    };

    const existing = find();
    if (existing && attach(existing)) {
      return () => cleanup?.();
    }

    const observer = new MutationObserver(() => {
      if (disposed) return;
      const t = find();
      if (t && attach(t)) {
        observer.disconnect();
        clearTimeout(giveUp);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    // Bounded: the export lands in well under a second on a warm build, and a page that carries no
    // mosaic must not hold an observer over the whole document for the life of the visit.
    const giveUp = setTimeout(() => observer.disconnect(), 20000);

    return () => {
      disposed = true;
      clearTimeout(giveUp);
      observer.disconnect();
      cleanup?.();
    };
  }, []);

  return null;
}
